import { useCallback } from 'react';
import { useTracking } from 'react-tracking';

const shapeTrackingData = trackingData => {
    const defaultTrackingData = {
        type: getTrackingType(trackingData),
        location: getTrackingLocation(trackingData),
        user: getTrackingUser(trackingData),
        click: getTrackingClick(trackingData),
        search: getTrackingSearch(trackingData),
        excludeService: trackingData?.excludeService,
        unfiltered: { ...trackingData },
    };
    return defaultTrackingData;
};

const getTrackingType = data => {
    let type = 'view';
    if (data?.type === 'conversion') type = 'conversion';

    if (data?.login) {
        type = 'login';
        return type;
    }

    if (data?.click) {
        type = 'click';
        if (data.click.type === 'conversion') type = 'conversion';
        return type;
    }

    if (data?.search) {
        type = 'search';
        if (data.search.type === 'conversion') type = 'conversion';
        return type;
    }

    return type;
};

const getTrackingLocation = data => {
    const locationNotNeeded = !!data?.login;
    if (locationNotNeeded) return;

    const location = {
        host: data?.location?.hostname || window.location.hostname,
        pathname: data?.location?.pathname || window.location.pathname,
        query_params: data?.location?.search || window.location.search,
    };

    return location;
};

/**
 * It's important to not track uniquely identifiable information
 * of any user, per Google IPP, GDPR, and ICSC privacy policies.
 */
const getTrackingUser = data => {
    if (!data?.viewer) return;

    const flatSecondaryBusinesses = data.viewer?.company?.secondaryBusinessTypes
        ? data.viewer.company.secondaryBusinessTypes.flatMap((type, key) => [
              { [`secondary_business_type_${key}_code`]: type.code },
              { [`secondary_business_type_${key}_name`]: type.name },
          ])
        : [{ secondary_business_type_0_code: '' }, { secondary_business_type_0_name: '' }];

    const events = data.viewer.events ? data.viewer.events.filter(Boolean) : null;
    const firstFiveEvents = events && Object.keys(events).length > 5 ? events.slice(0, 5) : events;
    const flatEvents = firstFiveEvents
        ? firstFiveEvents.flatMap((event, key) => [{ [`event_code_${key + 1}`]: event.code }])
        : [{ not_attending_events: 'true' }];

    const user = {
        // Strings
        member_type: data.viewer.memberType,
        status: data.viewer.status,
        primary_business_type_code: data.viewer?.company?.primaryBusinessType.code,
        primary_business_type_name: data.viewer?.company?.primaryBusinessType.name,
        // Booleans
        minority_owned_business: JSON.stringify(data.viewer?.company?.minorityOwned),
        women_owned_business: JSON.stringify(data.viewer?.company?.womanOwned),
        displaying_email: JSON.stringify(data.viewer.displayEmail),
        displaying_photo: JSON.stringify(data.viewer.displayPhoto),
        has_subscription: JSON.stringify(data.viewer.hasSubscription),
        meeting_invites: JSON.stringify(data.viewer.meetingInvites),
        messages_enabled: JSON.stringify(data.viewer.messages),
        official_member: JSON.stringify(data.viewer.official),
        student_member: JSON.stringify(data.viewer.studentMember),
    };

    // Flattened arrays
    Object.assign(user, ...flatSecondaryBusinesses, ...flatEvents);

    return user;
};

const getTrackingClick = data => {
    if (!data?.click) return { type: '', target: '', value: '' };

    const click = {
        type: data.click.type,
        target: data.click.target,
        value: data.click.value,
    };

    return click;
};

const getTrackingSearch = data => {
    if (!data?.search) return { type: '', query: '' };

    const search = {
        type: data.search.type,
        query: data.search.query,
    };

    return search;
};

/**
 * An extension of react-tracking that sets sane defaults
 * with the tracking data passed to it.
 *
 * This is the primary function used to track unique events in this app.
 *
 * @see https://github.com/nytimes/react-tracking
 * @param trackingData represents the data to be tracked (or a function returning that data)
 * @param options is an optional object that accepts configuration options
 */
const useIcscTracking = (trackingData, options) => {
    const { Track, trackEvent, getTrackingData } = useTracking(trackingData, options);

    const trackEventWithDefaults = useCallback(
        trackParams => {
            const readyTrackingData = shapeTrackingData(trackParams);
            trackEvent(readyTrackingData);
        },
        [trackEvent]
    );

    return { Track, trackEvent: trackEventWithDefaults, getTrackingData };
};

export default useIcscTracking;
